import { get as getMetadata, ref as metadataRef } from 'firebase/database'
import {
  endAt,
  endBefore,
  get,
  limitToFirst,
  limitToLast,
  onValue,
  orderByChild,
  query,
  ref,
  startAfter,
  startAt,
  type Unsubscribe,
} from '../../services/dashboard-read-view'

import { getFirebaseServices } from '../../services/firebase'
import type { AppointmentView } from './appointments-model'
import {
  APPOINTMENT_QUERY_SIZE,
  type AppointmentCursor,
} from './appointments-pagination'

export type AppointmentsRepository = {
  subscribeAppointments: (
    tenantId: string,
    view: AppointmentView,
    nowMs: number,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
  getMoreAppointments: (
    tenantId: string,
    view: AppointmentView,
    cursor: AppointmentCursor
  ) => Promise<unknown>
  subscribeAppointmentsNeedingReview: (
    tenantId: string,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
}

export type AppointmentWindowRepository = {
  getClinicTimeZone: (tenantId: string) => Promise<unknown>
  getClinicScheduleSettings: (tenantId: string) => Promise<unknown>
  subscribeAppointmentWindow: (
    tenantId: string,
    startAtMs: number,
    endBeforeMs: number,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
  /** Every covered clinic day's last complete NexHealth read (PHI-free). */
  subscribeAppointmentCoverage?: (
    tenantId: string,
    value: (snapshot: unknown) => void,
    error: (error: unknown) => void
  ) => Unsubscribe
}

function appointmentsRef(tenantId: string) {
  return ref(
    getFirebaseServices().database,
    `dashboardAppointments/${tenantId}`
  )
}

async function queryValue(queryReference: ReturnType<typeof query>) {
  const snapshot = await get(queryReference)
  return snapshot.exists() ? (snapshot.val() as unknown) : null
}

export const firebaseAppointmentsRepository: AppointmentsRepository &
  AppointmentWindowRepository = {
  async getClinicTimeZone(tenantId) {
    const snapshot = await getMetadata(
      metadataRef(
        getFirebaseServices().database,
        `clinic/${tenantId}/settings/timeZone`
      )
    )
    return snapshot.exists() ? (snapshot.val() as unknown) : null
  },

  async getClinicScheduleSettings(tenantId) {
    const [workingHours, specialDays] = await Promise.all([
      getMetadata(
        metadataRef(
          getFirebaseServices().database,
          `clinic/${tenantId}/settings/workingHours`
        )
      ),
      getMetadata(
        metadataRef(
          getFirebaseServices().database,
          `clinic/${tenantId}/settings/specialDays`
        )
      ),
    ])
    return {
      workingHours: workingHours.exists()
        ? (workingHours.val() as unknown)
        : null,
      specialDays: specialDays.exists() ? (specialDays.val() as unknown) : null,
    }
  },

  subscribeAppointmentWindow(
    tenantId,
    startAtMs,
    endBeforeMs,
    handleValue,
    handleError
  ) {
    const windowQuery = query(
      appointmentsRef(tenantId),
      orderByChild('scheduledAtMs'),
      startAt(startAtMs),
      endBefore(endBeforeMs)
    )
    return onValue(
      windowQuery,
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },
  subscribeAppointmentCoverage(tenantId, handleValue, handleError) {
    return onValue(
      ref(
        getFirebaseServices().database,
        `dashboardAppointmentCoverage/v2/${tenantId}`
      ),
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },
  subscribeAppointments(tenantId, view, nowMs, handleValue, handleError) {
    const base = appointmentsRef(tenantId)
    const appointmentsQuery =
      view === 'upcoming'
        ? query(
            base,
            orderByChild('scheduledAtMs'),
            startAt(nowMs),
            limitToFirst(APPOINTMENT_QUERY_SIZE)
          )
        : query(
            base,
            orderByChild('scheduledAtMs'),
            startAt(1),
            endBefore(nowMs),
            limitToLast(APPOINTMENT_QUERY_SIZE)
          )
    return onValue(
      appointmentsQuery,
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },

  getMoreAppointments(tenantId, view, cursor) {
    const base = appointmentsRef(tenantId)
    return view === 'upcoming'
      ? queryValue(
          query(
            base,
            orderByChild('scheduledAtMs'),
            startAfter(cursor.scheduledAtMs, cursor.appointmentId),
            limitToFirst(APPOINTMENT_QUERY_SIZE)
          )
        )
      : queryValue(
          query(
            base,
            orderByChild('scheduledAtMs'),
            startAt(1),
            endBefore(cursor.scheduledAtMs, cursor.appointmentId),
            limitToLast(APPOINTMENT_QUERY_SIZE)
          )
        )
  },

  subscribeAppointmentsNeedingReview(tenantId, handleValue, handleError) {
    const reviewQuery = query(
      appointmentsRef(tenantId),
      orderByChild('scheduledAtMs'),
      endAt(null),
      limitToLast(APPOINTMENT_QUERY_SIZE)
    )
    return onValue(
      reviewQuery,
      (snapshot) => handleValue(snapshot.exists() ? snapshot.val() : null),
      handleError
    )
  },
}

export function appointmentReadError(
  error: unknown
): 'forbidden' | 'transport' {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = String(error.code).toLowerCase()
    if (
      code.includes('permission-denied') ||
      code.includes('permission_denied')
    ) {
      return 'forbidden'
    }
  }
  return 'transport'
}
